import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Anchor,
  Avatar,
  Badge,
  Button,
  Card,
  Container,
  Flex,
  Group,
  Paper,
  PasswordInput,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { motion } from "framer-motion";
import { ShieldCheck, Sparkles, UsersRound } from "lucide-react";

import { useToastNotification } from "../../../hooks/useToast";
import { AppName, HttpCodes } from "../../../lib/data";
import { Endpoints } from "../../../lib/endpoints";
import { validateEmail } from "../../../lib/methods";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { NonPaginatedResponse } from "../../../types";
import { IUserProps } from "../../../types/User";
import "./styles.scss";

const heroStats = [
  { label: "Active organizers", value: "8.4K" },
  { label: "Processed tickets", value: "2.9M" },
  { label: "On-site conversions", value: "62%" },
];

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const toast = useToastNotification();
  const loginForm = useForm({
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value: string) => (validateEmail(value) ? null : "Invalid email"),
      password: (value: string) =>
        value.length >= 6 ? null : "Password must be at least 6 characters",
    },
  });

  const HandleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { hasErrors } = loginForm.validate();
    if (!hasErrors) {
      setLoading(true);
      const { email, password } = loginForm.values;
      const r = await PerformRequest<
        NonPaginatedResponse<{
          user: IUserProps;
          refresh_token: string;
          access_token: string;
        }>
      >({
        method: Endpoints.GetLogin().method,
        route: Endpoints.GetLogin().route,
        data: {
          email,
          password,
        },
        onError: () => setLoading(false),
      });
      if (r) {
        if (r.status === HttpCodes.BadRequest) {
          toast.error(r.message ?? "Invalid credentials");
        }
        if (r.status === HttpCodes.Success) {
          localStorage.setItem("t", r.data?.data?.access_token);
          localStorage.setItem("r", r.data?.data?.refresh_token);
          navigate("/d");
        }
      }

      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__background">
        <span className="glow glow--one" />
        <span className="glow glow--two" />
        <span className="grid-overlay" />
        <span className="noise" />
      </div>

      <Container size="xl" className="login-page__container">
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
          <Stack className="login-page__hero" gap="xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge
                variant="gradient"
                gradient={{ from: "#ab7cff", to: "#57d2ff" }}
                size="lg"
                radius="md"
                leftSection={<Sparkles size={16} />}
              >
                {AppName}
              </Badge>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              <Title order={1} className="login-page__title">
                Operate{" "}
                <Text component="span" inherit className="gradient-text">
                  immersive events
                </Text>{" "}
                with zero friction
              </Title>
              <Text size="lg" c="dimmed" maw={540} opacity={0.9}>
                The same toolkit powering{" "}
                <Text component="span" fw={600} c="dark">
                  {AppName?.toLowerCase()}.dev
                </Text>{" "}
                —dynamic flows, live occupancy, and on-brand comms in one panel.
              </Text>
            </motion.div>

            <motion.div
              className="login-page__stats-grid"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
            >
              {heroStats.map((stat) => (
                <div key={stat.label} className="login-page__stat-card">
                  <Text c="dark" fw={700} fz="xl">
                    {stat.value}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {stat.label}
                  </Text>
                </div>
              ))}
            </motion.div>
          </Stack>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6 }}
          >
            <Paper
              shadow="xl"
              radius="lg"
              p="xl"
              className="login-page__card"
              withBorder
            >
              <Stack gap="md">
                <div>
                  <Title order={3}>Log in to continue</Title>
                  <Text size="sm" c="dimmed">
                    Authenticate to orchestrate venues, passes, and live
                    touchpoints.
                  </Text>
                </div>

                <form onSubmit={HandleLogin}>
                  <Stack gap="md">
                    <TextInput
                      label="Email address"
                      placeholder="you@company.com"
                      size="lg"
                      radius="sm"
                      leftSection={<UsersRound size={16} />}
                      {...loginForm.getInputProps("email")}
                    />
                    <PasswordInput
                      label="Password"
                      placeholder="••••••••"
                      size="lg"
                      radius="sm"
                      {...loginForm.getInputProps("password")}
                    />

                    <Group justify="space-between" align="center" mt="sm">
                      <Anchor
                        c="cyan"
                        size="sm"
                        component={Link}
                        to="/forgot-password"
                      >
                        Forgot password?
                      </Anchor>
                      <Button
                        type="submit"
                        radius="sm"
                        size="lg"
                        className="login-page__primary-button"
                        fullWidth={false}
                        loading={isLoading}
                      >
                        Login
                      </Button>
                    </Group>
                  </Stack>
                </form>

                <Card radius="md" className="login-page__status" withBorder>
                  <Group justify="space-between">
                    <Text size="sm" fw={600}>
                      Live Uptime
                    </Text>
                    <Badge color="green" variant="dot">
                      99.98%
                    </Badge>
                  </Group>
                  <Text size="xs" c="dimmed" mt={4}>
                    Infrastructure health updated every 30 seconds.
                  </Text>
                </Card>

                <Text size="sm" c="dimmed" ta="center">
                  Don&apos;t have an account?{" "}
                  <Anchor component={Link} to="/register" size="sm" c="cyan">
                    Create one
                  </Anchor>
                </Text>
              </Stack>
            </Paper>
          </motion.div>
        </SimpleGrid>
      </Container>
    </div>
  );
};

export default Login;
